// Translates between react-querybuilder's native rule-tree shape and the CDP segment
// API's segment_json. Only used at the API boundary — the query builder itself keeps
// using the react-querybuilder shape everywhere else.
//
// Corrected per the Segment API doc: segment_json is NOT a generic op-key shape.
// fn_compile_rule accepts exactly { combinator: "and"|"or", rules: [...] } nesting
// { field, operator, value } leaves — the same keys and the same literal operator
// strings (e.g. "is greater than", "isbetween", "Is") that queryPages/field.js already
// uses. field/operator values must be exactly what GET /segments/registry lists for
// this tenant; there is no translation step, so these functions are now effectively
// a structural pass-through (stripping incidental react-querybuilder keys like `id`)
// rather than a vocabulary conversion. A wrong field/operator isn't caught by request
// validation — it fails later as a 500 when the database compiles the rule tree.

const isGroup = node => node && Array.isArray(node.rules)

let ruleIdCounter = 0
const nextRuleId = () => `rqb-${Date.now().toString(36)}-${(ruleIdCounter++).toString(36)}`

/**
 * Gives the group and every nested rule/group a stable `id` (existing ids are kept).
 * react-querybuilder regenerates ids on EVERY render for a query without ids, which
 * remounts each rule's value editor. Remounting the tags editor refetches tags, the
 * fetch updates context, the builder re-renders with new ids again - an endless
 * Audience Tags API loop. Call this once when putting a query from the API into state.
 * @param {Object} query { combinator, rules }
 */
export const withRuleIds = query => {
  if (!isGroup(query)) {
    return query
  }

  return {
    ...query,
    id: query.id || nextRuleId(),
    rules: query.rules.map(rule => {
      if (typeof rule === 'string') {
        return rule
      } // independent combinator ('and' / 'or')

      return isGroup(rule) ? withRuleIds(rule) : { ...rule, id: rule.id || nextRuleId() }
    })
  }
}

// --- Independent combinators (an AND/OR choice per row) ---
//
// The segment builders use react-querybuilder's "independent combinators" shape, where a
// group's rules interleave rules and combinator strings: { rules: [A, 'and', B, 'or', C] }.
// That lets each AND/OR dropdown change only its own spot. segment_json (and everything
// that reads segmentQueryJson for the API) uses one combinator per group instead:
// { combinator, rules }. fromICQuery converts with the usual precedence - AND binds tighter
// than OR - so A AND B OR C becomes { or: [ { and: [A, B] }, C ] }.

const hasInlineCombinators = group => isGroup(group) && group.rules.some(rule => typeof rule === 'string')

/** { combinator, rules } -> independent-combinator group (already-IC input is returned as is). */
export const toICQuery = group => {
  if (!isGroup(group)) {
    return group
  }
  // react-querybuilder only treats a group as independent-combinator when it has no
  // `combinator` key, so it's always dropped here.
  const { combinator = 'and', ...rest } = group
  if (hasInlineCombinators(group)) {
    return { ...rest, rules: group.rules.map(rule => (isGroup(rule) ? toICQuery(rule) : rule)) }
  }

  const rules = []
  group.rules.forEach((rule, index) => {
    if (index > 0) {
      rules.push(combinator)
    }
    rules.push(isGroup(rule) ? toICQuery(rule) : rule)
  })

  return { ...rest, rules }
}

/** Independent-combinator group -> { combinator, rules } (AND binds tighter than OR). */
export const fromICQuery = group => {
  if (!isGroup(group)) {
    return group
  }
  if (!hasInlineCombinators(group)) {
    return {
      ...group,
      combinator: group.combinator || 'and',
      rules: group.rules.map(rule => (isGroup(rule) ? fromICQuery(rule) : rule))
    }
  }

  // Split into OR-separated chunks; rules inside a chunk are joined by AND.
  const chunks = [[]]
  let expectRule = true
  group.rules.forEach(item => {
    if (typeof item === 'string') {
      if (!expectRule && item.toLowerCase() === 'or') {
        chunks.push([])
      }
      expectRule = true

      return
    }
    chunks[chunks.length - 1].push(isGroup(item) ? fromICQuery(item) : item)
    expectRule = false
  })

  const orParts = chunks.filter(chunk => chunk.length > 0)
  const { combinator: _combinator, ...rest } = group
  if (orParts.length <= 1) {
    return { ...rest, combinator: 'and', rules: orParts[0] || [] }
  }

  return {
    ...rest,
    combinator: 'or',
    rules: orParts.map(chunk => (chunk.length === 1 ? chunk[0] : { combinator: 'and', rules: chunk }))
  }
}

// --- Several tags in one rule ---
//
// The tags editor always lets a rule hold several tags, but unless the registry marks
// tags value_type "multiselect", the segment compiler reads a rule's value as ONE exact
// tag name. So on the way out a multi-tag rule is expanded into a group of single-tag
// rules: "is tagged [vip, trial]" -> tagged vip OR tagged trial (any of them), and
// "is not tagged [vip, trial]" -> not tagged vip AND not tagged trial (none of them).
// On the way in, a group of exactly that shape is folded back into one rule.
const TAG_FIELD = 'tags'
const TAG_EXPAND_COMBINATORS = { isTagged: 'or', isNotTagged: 'and' }

const toApiRule = rule => {
  const { field, operator, value } = rule
  if (field !== TAG_FIELD || !Array.isArray(value) || isMultiSelectField(TAG_FIELD)) {
    return { field, operator, value }
  }
  if (value.length <= 1) {
    return { field, operator, value: value[0] || '' }
  }

  return {
    combinator: TAG_EXPAND_COMBINATORS[operator] || 'or',
    rules: value.map(tag => ({ field, operator, value: tag }))
  }
}

const foldTagGroup = group => {
  const [first] = group.rules
  const isFoldable =
    group.rules.length > 1 &&
    TAG_EXPAND_COMBINATORS[first.operator] === (group.combinator || 'and').toLowerCase() &&
    group.rules.every(
      rule =>
        !isGroup(rule) &&
        rule.field === TAG_FIELD &&
        rule.operator === first.operator &&
        typeof rule.value === 'string' &&
        rule.value !== ''
    )

  return isFoldable ? { field: TAG_FIELD, operator: first.operator, value: group.rules.map(rule => rule.value) } : null
}

const toApiGroup = group => ({
  combinator: group.combinator || 'and',
  rules: group.rules.map(rule => (isGroup(rule) ? toApiGroup(rule) : toApiRule(rule)))
})

/**
 * react-querybuilder tree (standard or independent-combinator shape) -> CDP API segment_json.
 * @param {Object} rqbQuery { combinator, rules } or { rules: [rule, 'and', rule, ...] }
 */
export const toApiSegmentJson = rqbQuery => {
  if (!isGroup(rqbQuery)) {
    return { combinator: 'and', rules: [] }
  }

  return toApiGroup(fromICQuery(rqbQuery))
}

/**
 * CDP API segment_json -> react-querybuilder tree.
 * @param {Object} apiJson { combinator, rules }
 */
export const fromApiSegmentJson = apiJson => {
  if (!isGroup(apiJson)) {
    return { combinator: 'and', rules: [] }
  }
  const foldedRoot = foldTagGroup(apiJson)
  if (foldedRoot) {
    return { combinator: 'and', rules: [foldedRoot] }
  }

  const rules = apiJson.rules.map(rule =>
    isGroup(rule)
      ? foldTagGroup(rule) || fromApiSegmentJson(rule)
      : {
          field: rule.field,
          operator: rule.operator,
          value: rule.value
        }
  )

  return {
    combinator: apiJson.combinator || 'and',
    rules
  }
}

// react-querybuilder's own DefaultValueEditor hides the value input only for the
// literal operators "null"/"notNull" (hardcoded in the library, not schema-driven).
// The registry's operators catalogue marks every operator that needs no value at all
// with value_kind: "none" - as of the last registry pull that's "null", "notNull",
// "isEmpty", "isNotEmpty", "isMostEngaged", "isMultiChannel", "isOnlyEmail",
// "isOnlyPhone", "isOnlyWhatsapp", "isToday", "isYesterday", "isTomorrow",
// "isThisCalendarMonth", "isPreviousCalendarMonth", "isNextCalendarMonth" - none of
// which react-querybuilder recognizes on its own. Every custom valueEditor dispatch
// and rule validator checks this list first so those operators hide the input and
// don't get marked invalid for having no value, instead of hand-maintaining this list
// each time the backend adds another "none" operator, it's refreshed from the live
// registry response as soon as GET /segments/registry resolves (see
// syncNoValueOperatorsFromRegistry below) - this fallback only covers whatever's
// already been confirmed live, for the brief window before that first fetch completes.
export let NO_VALUE_OPERATORS = [
  'null',
  'notNull',
  'isEmpty',
  'isNotEmpty',
  'isMostEngaged',
  'isMultiChannel',
  'isOnlyEmail',
  'isOnlyPhone',
  'isOnlyWhatsapp',
  'isToday',
  'isYesterday',
  'isTomorrow',
  'isThisCalendarMonth',
  'isPreviousCalendarMonth',
  'isNextCalendarMonth'
]

// Refreshes NO_VALUE_OPERATORS from the registry's own operators[].value_kind === "none"
// entries (including their aliases) so it tracks the backend instead of drifting stale
// again. A no-op if the registry has no operators (e.g. a failed fetch), so the static
// fallback above keeps applying.
export const syncNoValueOperatorsFromRegistry = registry => {
  const fromRegistry = (registry?.operators || [])
    .filter(op => op.value_kind === 'none')
    .flatMap(op => [op.op_key, ...(op.aliases || [])])

  if (fromRegistry.length > 0) {
    NO_VALUE_OPERATORS = fromRegistry
  }
}

// The value input for a rule is decided by the registry itself: the field's value_type
// (text | number | date) combined with the operator's value_kind (none | one | two | list).
// e.g. clicks_60d (number) + isbetween (two) -> two number inputs, created_on (date) +
// isbetween (two) -> two date pickers. These fallbacks cover the window before the first
// GET /segments/registry resolves; syncValueInputsFromRegistry replaces them with the live values.
let OPERATOR_VALUE_KINDS = {
  isbetween: 'two',
  between: 'two',
  notbetween: 'two',
  notBetween: 'two',
  aParticularCampaign: 'list',
  isOneOf: 'list',
  in: 'list',
  isNotOneOf: 'list',
  notIn: 'list'
}
let FIELD_VALUE_TYPES = {
  created_on: 'date',
  last_open_date: 'date',
  last_click_date: 'date',
  email_open: 'date',
  link_click: 'date',
  date_of_birth: 'date',
  last_convert_date: 'date'
}

// Per field_key (and alias), refreshed from the registry by syncValueInputsFromRegistry:
// closed value sets (`values`, in registry order), numeric bounds (`min`/`max`/`step`, kept as
// the registry's strings - never round-tripped through a float, which turns 0.0001 into 1e-4)
// display names, and `value_labels` (what a coded value means, e.g. "-1" -> "Unsubscribed").
// Empty until the first GET /segments/registry resolves.
let FIELD_VALUE_SETS = {}
let FIELD_NUMBER_BOUNDS = {}
let FIELD_UI_NAMES = {}
let FIELD_VALUE_LABEL_MAPS = {}

export const getFieldValueSet = field => FIELD_VALUE_SETS[field] || null
// value_type "multiselect" (e.g. tags) lets one rule hold several values instead of one.
export const isMultiSelectField = field => FIELD_VALUE_TYPES[field] === 'multiselect'
export const getFieldNumberBounds = field => FIELD_NUMBER_BOUNDS[field] || {}
export const getRegistryFieldName = field => FIELD_UI_NAMES[field] || ''
export const getFieldValueLabels = field => FIELD_VALUE_LABEL_MAPS[field] || null

// `value_labels` is absent (not {}) when a field has none; only a plain object counts.
const pickValueLabels = field =>
  field?.value_labels && typeof field.value_labels === 'object' && !Array.isArray(field.value_labels)
    ? field.value_labels
    : null

const pickNumberBounds = field => {
  const bounds = {}
  ;['min', 'max', 'step'].forEach(key => {
    // An absent bound stays unset - most numbers are unbounded on at least one side.
    if (field[key] !== undefined && field[key] !== null && field[key] !== '') {
      bounds[key] = String(field[key])
    }
  })

  return bounds
}

export const syncValueInputsFromRegistry = registry => {
  const operatorKinds = {}
  ;(registry?.operators || []).forEach(op => {
    ;[op.op_key, ...(op.aliases || [])].forEach(key => {
      // Keep the first registration when an alias collides with a real op_key (e.g. "in").
      if (!(key in operatorKinds)) {
        operatorKinds[key] = op.value_kind
      }
    })
  })

  const fieldTypes = {}
  const valueSets = {}
  const numberBounds = {}
  const uiNames = {}
  const valueLabelMaps = {}
  ;(registry?.fields || []).forEach(field => {
    const values = Array.isArray(field.values) && field.values.length > 0 ? field.values.map(String) : null
    const bounds = pickNumberBounds(field)
    const valueLabels = pickValueLabels(field)
    ;[field.field_key, ...(field.aliases || [])].forEach(key => {
      fieldTypes[key] = field.value_type
      if (values) {
        valueSets[key] = values
      }
      if (Object.keys(bounds).length) {
        numberBounds[key] = bounds
      }
      if (field.ui_name) {
        uiNames[key] = field.ui_name
      }
      if (valueLabels) {
        valueLabelMaps[key] = valueLabels
      }
    })
  })

  if (Object.keys(operatorKinds).length > 0) {
    OPERATOR_VALUE_KINDS = operatorKinds
  }
  if (Object.keys(fieldTypes).length > 0) {
    FIELD_VALUE_TYPES = fieldTypes
    FIELD_VALUE_SETS = valueSets
    FIELD_NUMBER_BOUNDS = numberBounds
    FIELD_UI_NAMES = uiNames
    FIELD_VALUE_LABEL_MAPS = valueLabelMaps
  }
}

export const getOperatorValueKind = operator => {
  if (NO_VALUE_OPERATORS.includes(operator)) {
    return 'none'
  }
  return OPERATOR_VALUE_KINDS[operator] || 'one'
}

/**
 * Which value input a rule needs, per the registry's value_type x value_kind mapping.
 * @returns 'none' | 'text' | 'number' | 'date' | 'numberRange' | 'dateRange' | 'multiselect' | 'select'
 *          | 'campaign' | 'tags' | 'month'
 * @param {string} field field_key
 * @param {string} operator op_key
 * @param {string} [fallbackValueType] used when the registry hasn't loaded (e.g. props.inputType)
 */
export const resolveValueInput = (field, operator, fallbackValueType) => {
  const valueKind = getOperatorValueKind(operator)
  if (valueKind === 'none') {
    return 'none'
  }

  if (operator === 'anyCampaign' || operator === 'aParticularCampaign') {
    return 'campaign'
  }
  if (operator === 'isTagged' || operator === 'isNotTagged') {
    return 'tags'
  }
  if (operator === 'isInMonth') {
    return 'month'
  }

  const valueType = FIELD_VALUE_TYPES[field] || fallbackValueType || 'text'

  // A closed set is always picked from a list, whatever its value_type (email_optout_level
  // is a number with values -1/0/1).
  if (valueKind === 'one' && FIELD_VALUE_SETS[field]) {
    return 'select'
  }

  if (valueKind === 'two') {
    return valueType === 'date' ? 'dateRange' : 'numberRange'
  }
  if (valueKind === 'list') {
    return 'multiselect'
  }
  if (valueType === 'date') {
    return 'date'
  }
  if (valueType === 'number') {
    return 'number'
  }

  return 'text'
}

// The old /a/segments/predefinedsegments endpoint baked a synthetic "Custom" card into
// its response so the segment-selection screen could render a "build your own" button.
// The new kind=predefined list only returns real predefined segments, so that card is
// synthesized here instead.
export const CUSTOM_SEGMENT_TEMPLATE = {
  id: 'custom',
  name: 'Custom',
  description: '',
  audience_count: null,
  updated_at: null,
  query_json: null
}

// Maps one CDP segment API object to also carry the legacy field names the UI already
// reads (id, query, query_json, last_run), so existing components don't need to change.
export const fromApiSegment = apiSegment => {
  if (!apiSegment) {
    return apiSegment
  }

  return {
    ...apiSegment,
    id: apiSegment.segment_id ?? apiSegment.id,
    query: apiSegment.compiled_sql ?? apiSegment.query,
    query_json: apiSegment.segment_json ? fromApiSegmentJson(apiSegment.segment_json) : apiSegment.query_json,
    // "Has this been run" comes from last_executed_time only. The API's own last_run is when
    // the count was last taken (and the Go zero time 0001-01-01 when never), so it is not used
    // as a fallback; the listing's "Count as of" reads audience_count_at instead.
    last_run: apiSegment.last_executed_time ?? null
  }
}

// Builds the CDP segment API's create/update request body from the wizard's state.
export const toApiSegmentPayload = ({
  name,
  type,
  description,
  prompt,
  segmentQueryJson,
  applicableChannels,
  tags
}) => ({
  name,
  type,
  description: description ?? null,
  prompt,
  segment_json: toApiSegmentJson(segmentQueryJson || { combinator: 'and', rules: [] }),
  applicable_channels: applicableChannels || [],
  tags
})

// --- GET /segments/registry -> react-querybuilder `fields` prop ---
//
// The registry only gives an op_key string (e.g. "is greater than", "isEmpty") with no
// human label, so known op_keys are mapped back to the exact labels queryPages/field.js
// already used (zero visual change for anything the registry and the old hardcoded
// catalogue agree on); an op_key the registry introduces that isn't in this table falls
// back to a humanized version of the key itself rather than breaking.
const OPERATOR_LABELS = {
  Is: 'Is',
  'is not': 'Is Not',
  contains: 'contains',
  doesNotContain: 'does not contain',
  startsWith: 'starts with',
  endsWith: 'ends with',
  null: 'is blank',
  notNull: 'is not blank',
  isEmpty: 'is blank',
  isNotEmpty: 'is not blank',
  'is less than': 'is less than',
  'is greater than': 'is greater than',
  between: 'between',
  on: 'on',
  'not on': 'not on',
  'is after': 'is after',
  'is at or after': 'is at or after',
  'is before': 'is before',
  'is at or before': 'is at or before',
  isbetween: 'is between',
  notbetween: 'is not between',
  isTagged: 'Contact is tagged',
  isNotTagged: 'Contact is not tagged',
  isMultiChannel: 'Is Multi-Channel',
  isOnlyEmail: 'Is Only Email',
  isOnlyPhone: 'Is Only Phone',
  isOnlyWhatsapp: 'Is Only Whatsapp',
  isMostEngaged: 'Most Engaged',
  reachableOn: 'Is Reachable On',
  anyCampaign: 'Any campaign',
  aParticularCampaign: 'a particular campaign'
}

const humanizeOperatorLabel = opKey =>
  OPERATOR_LABELS[opKey] || opKey.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase())

// Fields whose value editor is a special widget selected by field name (tags picker,
// campaign selector, operator-only channel/engagement fields) rather than a generic
// text/number/date input - unchanged from the old hardcoded field.js so the existing
// renderValueEditor dispatch in each query-builder screen keeps matching them as-is.
// reachable_channel is not listed: its reachableOn operator takes one channel from the
// field's values (the other channel operators are value_kind "none").
const NO_VALUE_EDITOR_FIELD_KEYS = ['engagement']
const CAMPAIGN_FIELD_KEYS = ['was_sent', 'was_not_sent', 'clicked', 'not_clicked', 'opened', 'not_opened']

// Display labels for closed-set values (registry `values`). The value sent to the API is
// always the raw registry string - never the label, which the compiler rejects ("expects a
// number, got Unsubscribed"). The registry's own `value_labels` wins; otherwise these generic
// spellings, then title case (male -> Male).
const VALUE_LABELS = {
  sms: 'SMS',
  rcs: 'RCS',
  whatsapp: 'WhatsApp',
  instagram_dm: 'Instagram DM'
}

// `valueLabels` is the field's registry map; pass it when you hold the field entry, otherwise
// it is looked up by field_key from the last registry sync. Keyed by the value exactly as it
// appears in `values` (a string), so a stored number (-1) is looked up as "-1".
export const humanizeValueLabel = (value, fieldKey, valueLabels = FIELD_VALUE_LABEL_MAPS[fieldKey]) =>
  valueLabels?.[String(value)] ??
  VALUE_LABELS[value] ??
  String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())

// A field's closed set of values, in registry order (meaningful - e.g. channels by bit),
// as react-querybuilder options. `values` absent or empty means free text, so this returns
// null rather than an empty list. Older registries nested { name, label } under meta.values.
const getFieldValueOptions = field => {
  if (Array.isArray(field.values) && field.values.length > 0) {
    const valueLabels = pickValueLabels(field)

    // Iterate `values`, never the label map: its key order is not the registry's order, and
    // it may be missing an entry.
    return field.values.map(value => ({
      name: String(value),
      label: humanizeValueLabel(String(value), field.field_key, valueLabels)
    }))
  }
  if (Array.isArray(field.meta?.values) && field.meta.values.length > 0) {
    return field.meta.values.map(v => ({ name: v.name ?? v.value, label: v.label }))
  }

  return null
}

/**
 * GET /segments/registry response -> react-querybuilder `fields` array, grouped into
 * disabled heading rows by ui_group (matching the old hardcoded field.js layout).
 * @param {Object} registry { fields, operators }
 */
export const buildQueryBuilderFields = registry => {
  const registryFields = registry?.fields || []
  const orderedGroups = []
  const fieldsByGroup = new Map()

  registryFields.forEach(field => {
    const group = field.ui_group || 'Other'
    if (!fieldsByGroup.has(group)) {
      fieldsByGroup.set(group, [])
      orderedGroups.push(group)
    }
    fieldsByGroup.get(group).push(field)
  })

  const result = []

  orderedGroups.forEach(group => {
    result.push({ name: group, label: group, disabled: true })

    fieldsByGroup.get(group).forEach(field => {
      const isUnfilterable = field.filterable === false
      const entry = {
        name: field.field_key,
        label: isUnfilterable ? `${field.ui_name} (unavailable)` : field.ui_name,
        disabled: isUnfilterable,
        operators: (field.ops || []).map(opKey => ({ name: opKey, label: humanizeOperatorLabel(opKey) }))
      }

      if (field.value_type === 'number') {
        entry.inputType = 'number'
      }
      if (field.value_type === 'date') {
        entry.inputType = 'date'
      }
      Object.assign(entry, pickNumberBounds(field))

      const valueOptions = getFieldValueOptions(field)
      if (valueOptions) {
        entry.valueEditorType = 'select'
        entry.values = valueOptions
      } else if (field.field_key === 'tags') {
        entry.valueEditorType = 'multiselect'
      } else if (NO_VALUE_EDITOR_FIELD_KEYS.includes(field.field_key)) {
        entry.valueEditorType = null
      } else if (CAMPAIGN_FIELD_KEYS.includes(field.field_key)) {
        entry.valueEditorType = 'custom'
      }

      result.push(entry)
    })
  })

  return result
}
